import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { FileSignature, Printer, CheckCircle2 } from "lucide-react";
import { AgreementBody, printAgreement, STATUS } from "@/components/agreements/AgreementCommon";

export function FamilyAgreement() {
  const [rows, setRows] = useState(null);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/family/agreement").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows || rows.length === 0) return null;
  const pending = rows.find((r) => r.status === "sent");
  const current = rows.find((r) => r.status === "signed");
  const shown = pending || current;
  const sign = async () => {
    setBusy(true);
    try { await api.post(`/agreements/${pending.id}/sign`, { full_name: name, agree }); toast.success("Thank you — your agreement is signed"); setName(""); setAgree(false); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="family-agreement" className={`rounded-xl border bg-white p-5 shadow-sm ${pending ? "border-gold/50" : "border-border"}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-ink"><FileSignature size={20} className="text-brand" /> Service agreement</h2>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS[shown.status][1]}`} data-testid="family-agreement-status">Version {shown.version} · {STATUS[shown.status][0]}</span>
      </div>
      {pending && current && <p className="mb-3 rounded-lg bg-gold-light px-3 py-2 text-sm text-gold-dark">A new version is ready. Once you sign it, it replaces version {current.version}.</p>}
      <AgreementBody a={shown} />
      {pending ? (
        <div className="mt-4 space-y-3 rounded-lg border border-brand/30 bg-brand-light/30 p-4" data-testid="sign-agreement-form">
          <label className="flex items-start gap-2 text-sm text-ink"><input type="checkbox" className="mt-1 accent-brand" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="agree-checkbox" /> I have read and agree to this service agreement, including the terms and conditions, on behalf of {shown.client_name}.</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full name to sign" data-testid="sign-name-input" className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
          <button onClick={sign} disabled={busy || !agree || name.trim().length < 3} data-testid="sign-agreement-button" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-50"><CheckCircle2 size={17} /> {busy ? "Signing…" : "Sign agreement"}</button>
        </div>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        {rows.filter((r) => r.status !== "sent").map((r) => (
          <button key={r.id} onClick={() => printAgreement(r)} data-testid={`family-print-agreement-${r.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-ink hover:bg-secondary"><Printer size={13} /> Version {r.version} ({STATUS[r.status][0].toLowerCase()})</button>
        ))}
      </div>
    </section>
  );
}
